import Vue from 'vue'
import router from '@/router'
import get from 'lodash.get'

const getDefaultState = () => ({
  googleEcommerce: {
    transaction: {
      id: 'NONE',
      affiliation: process.env.VUE_APP_GOOGLE_ECOMMERCE_AFFILIATION, // NOT CHANGED!
      revenue: 0.0
    },
    items: []
  },
  // how many colors can be used until we have to use full width
  maxSingleWidthColors: 4,
  // notes for the new order
  orderNotes: '',
  // order summary for the new order
  orderSummary: null,
  // binding between product id and entered quantities
  productQuantities: {},
  // lookup for product date
  productSummary: {},
  // bool flags for single/double width of line based on # of colors
  productLinesFullWidth: {},
  // accordion logic
  visibleCategories: {},
  deliveryAddress: {}
})

const state = getDefaultState()

const getters = {
  // are we showing all items in full-width mode (for small screens)
  allFullWidth (state) {
    return state.maxSingleWidthColors < 4
  },
  formHasError (state, getters) {
    let hasError = false
    const orderForm = getters['orderForm']
    if (orderForm && orderForm.mainCategoriesList) {
      orderForm.mainCategoriesList.forEach((mc, mx) => {
        if (hasError) {
          return
        }
        mc.productGroupsList.forEach((pgl, gx) => {
          if (hasError) {
            return
          }
          pgl.productLinesList.forEach((pl, px) => {
            if (hasError) {
              return
            }
            const productRowKeys = Object.keys(pl).filter(key => key.startsWith('productRow'))
            for (let i = 0; i < productRowKeys.length; i++) {
              const key = productRowKeys[i]
              for (let j = 0; j < pl[key].productCellList.length; j++) {
                const pc = pl[key].productCellList[j]
                hasError = state.productQuantities[pc.product.id] !== null && state.productQuantities[pc.product.id] > 0 && state.productQuantities[pc.product.id] < pc.product.minimalOrderQuantity
                if (hasError) {
                  break
                }
              }
              if (hasError) {
                break
              }
            }
          })
        })
      })
    }
    return hasError
  },
  hasOrdedProducts (state, getters) {
    return Object.keys(getters.orderedProducts).length > 0
  },
  orderForm (state, getters, rootState, rootGetters) {
    // Old order form is deprecated, returning empty object for backward compatibility
    return {}
  },
  orderedProducts (state) {
    let res = {}
    Object.keys(state.productQuantities).forEach(k => {
      if (state.productQuantities[k] !== null && state.productQuantities[k] >= 0) {
        res[k] = state.productQuantities[k].toString()
      }
    })
    return res
  },
  quantitiesToOrder (state) {
    // productQuantities contains all product keys - eliminate those with nulls
    const res = {}
    Object.keys(state.productQuantities).forEach(k => {
      if (state.productQuantities[k] !== null && state.productQuantities[k] >= 0) {
        res[k] = state.productQuantities[k].toString()
      }
    })
    return res
  },
  deliveryAddress (state) {
    return state.deliveryAddress
  }
}

const actions = {
  // updates local state of vars
  // also can only refresh sizing flags on window resize
  refreshLookups ({ commit, state, rootState, getters }, { onlyLineSizes = false }) {
    const pq = {}
    const prodSummary = {}
    const lineSizes = {}
    let prevNewRow = false
    let prevFullWidth = false
    let newGroup = true
    if (getters.orderForm && getters.orderForm.mainCategoriesList) {
      getters.orderForm.mainCategoriesList.forEach((mc, mx) => {
        if (state.visibleCategories[mx] === undefined) {
          commit('setVisibleCategory', {
            category: mx,
            visible: false
          })
        }
        mc.productGroupsList.forEach((pgl, gx) => {
          newGroup = true
          let count = 0
          pgl.productLinesList.forEach((pl, px) => {
            const visible = !pl.containsSpecialOffer
            if (visible) {
              count++
            }
            // ------ SIZING CALCULATION
            // TODO: this is only for half/full layout, other types (table etc.) needed also
            // need full width if not all colors can fit
            const needFullWidth = getters.allFullWidth || pl.colorNumbersList.length > state.maxSingleWidthColors
            // when do we need to push product line in new row
            const newNewRow = !prevNewRow || newGroup || needFullWidth || prevFullWidth
            // does the current line has something right to it
            let hasNext = !needFullWidth && newNewRow && (px < pgl.productLinesList.length - 1)
            // it also depends if the next one is full width
            const nextNeedFullWidth = hasNext && pgl.productLinesList[px + 1].colorNumbersList.length > state.maxSingleWidthColors
            if (nextNeedFullWidth) {
              hasNext = false
            }
            // mx + '.' + gx + '.' + px  ==> fake an unique id for each product line
            lineSizes[mx + '.' + gx + '.' + px] = {
              fullWidth: needFullWidth,
              hasNextInSameRow: hasNext,
              newRow: newNewRow
            }
            prevNewRow = newNewRow
            prevFullWidth = needFullWidth
            newGroup = false
            // -------- LOOKUP STUFF - covers Main, TesterA and other productRowXXX cases
            const productRowKeys = Object.keys(pl).filter(key => key.startsWith('productRow'))
            productRowKeys.forEach(key => {
              pl[key].productCellList.forEach(pc => {
                pq[pc.product.id] = pc.quantity === 0 ? null : pc.quantity
                prodSummary[pc.product.id] = {
                  id: pc.product.id,
                  title: pc.product.nameDe
                }
              })
            })
          })
          pgl._visible = count > 0
        })
      })
    }
    commit('setLookups', {
      onlyLineSizes: onlyLineSizes,
      productQuantities: pq,
      productSummary: prodSummary,
      productLinesFullWidth: lineSizes
    })
  },
  async placeOrder ({ rootState, commit, dispatch, getters }) {
    const ok = await dispatch('orderFormService/createOrder', {
      note: state.orderNotes,
      productQuantitiesMap: getters.quantitiesToOrder,
      deliveryAddress: getters.deliveryAddress
    }, { root: true })
    if (ok) {
      // clear the state of the order
      commit('orderForm/clearProductQuantities', null, { root: true })
      router.push({ name: 'order-success' })
      await dispatch('authentication/authenticate', null, { root: true })

      const order = rootState.orderFormService.latestRequests.createOrder.response.order
      dispatch('orderForm/processGoogleEcommerceOrder', order, { root: true })
      dispatch('orderForm/processMatomoEcommerceOrder', { order, customer: rootState.authentication.customer }, { root: true })
      dispatch('orderFormService/getOrders', null, { root: true })
    } else {
      router.push({ name: 'order-failure' })
    }
  },
  processMatomoEcommerceOrder (_ctx, { order, customer }) {
    if (!window._paq || !order || !order.orderSummary) return
    const summary = order.orderSummary
    const isStudent = customer && customer.studentProducts === 1
    const parseEuro = (v) => {
      if (typeof v === 'number') return v
      if (!v) return 0
      const n = parseFloat(String(v).replace(/[^0-9,.-]/g, '').replace(',', '.'))
      return isNaN(n) ? 0 : n
    }
    const revenue = parseEuro(isStudent ? summary.totalPriceB2c : summary.totalPriceB2b)
    const tax = parseEuro(summary.vat)
    const shipping = parseEuro(summary.shippingCosts)
    const discount = parseEuro(summary.onlineDiscount)
    const subtotal = revenue - tax - shipping + discount
    ;(summary.orderProductsList || []).forEach(p => {
      const price = isStudent ? p.priceB2c : p.priceB2b
      window._paq.push([
        'addEcommerceItem',
        String(p.googleId || p.sapId || p.modelNumber || p.id || ''),
        p.nameDe || '',
        '',
        parseEuro(price),
        Number(p.quantity) || 0
      ])
    })
    window._paq.push([
      'trackEcommerceOrder',
      String(order.id),
      revenue,
      subtotal > 0 ? subtotal : revenue,
      tax,
      shipping,
      discount > 0 ? String(discount) : false
    ])
    window._paq.push(['trackEvent', 'b2b_order_form', 'OrderPlaced', String(order.id), revenue])
  },
  processGoogleEcommerceOrder ({ state, commit }, order) {
    if (!window.ga) {
      return
    }
    commit('prepareGoogleEcommerceData', order)
    const { transaction, items } = state.googleEcommerce
    window.ga('ecommerce:addTransaction', {
      'id': transaction.id, // Order.id
      'affiliation': transaction.affiliation,
      'revenue': transaction.revenue // Order.OrderSummary.total_price_b2b AS FLOAT
    })
    items.forEach(item => {
      window.ga('ecommerce:addItem', item)
    })
    // for (var itemKey in items) {
    //   if (items.hasOwnProperty(itemKey)) {
    //     window.ga('ecommerce:addItem', {
    //       'id': items[itemKey].id, // Order.id (use this again!! not the one of the product)
    //       'name': items[itemKey].name, // Order.OrderSummary.order_products.name_de
    //       'sku': items[itemKey].sku, // concat Order.OrderSummary.order_products.google_id
    //       'price': items[itemKey].price, // Order.OrderSummary.order_products.price_b2b it should already be float
    //       'quantity': items[itemKey].quantity // Order.OrderSummary.order_products.quantity
    //     })
    //   }
    // }
    window.ga('ecommerce:send')
    // window.ga('send', 'pageview') // DN: read somewhere that it expects it, but this also didn't work
  },
  setupGoogleEcommerce () {
    if (!window.ga) {
      return
    }
    window.ga('require', 'ecommerce', 'ecommerce.js')
    window.google_conversion_id = process.env.VUE_APP_GOOGLE_CONVERSION_ID
    window.google_conversion_language = 'de'
    window.google_conversion_format = '3'
    window.google_conversion_color = 'ffffff'
    window.google_conversion_label = process.env.VUE_APP_GOOGLE_CONVERSION_LABEL
    window.google_conversion_value = '0'
    window.google_custom_params = window.google_tag_params
    window.google_remarketing_only = true
  },
  async startOrderFlow ({ dispatch }) {
    dispatch('setupGoogleEcommerce')
    await dispatch('updateOrderSummary')
    router.push({ name: 'order-summary' })
  },
  async updateOrderSummary ({ commit, dispatch, rootState, rootGetters }) {
    let ok = await dispatch('orderFormService/getOrderSummary', {
      orderId: null,
      productQuantitiesMap: rootGetters['orderForm/orderedProducts']
    }, { root: true })
    if (ok) {
      const orderSummary = get(rootState, 'orderFormService.latestRequests.getOrderSummary.response.orderSummary', null)
      commit('setOrderSummary', orderSummary)
      commit('updateProductQuantitiesFromOrderSummary')
    } else {
      commit('setOrderSummary', null)
    }
    return ok
  }
}

const mutations = {
  clearProductQuantities (state) {
    state.orderNotes = ''
    Object.keys(state.productQuantities).forEach(k => {
      state.productQuantities[k] = null
    })
  },
  // based on orderSummary, prepare the transaction + items the processGoogleEcommerceOrder expects
  prepareGoogleEcommerceData (state, order) {
    state.googleEcommerce.transaction.id = order.id
    state.googleEcommerce.transaction.revenue = parseFloat(order.orderSummary.totalPriceB2b.replace('€', '').replace(',', '.'))
    const items = []
    order.orderSummary.orderProductsList.forEach(product => {
      items.push({
        id: order.id,
        name: product.nameDe,
        category: '',
        sku: product.googleId,
        price: product.priceB2b.toString(),
        quantity: product.quantity.toString()
      })
    })
    state.googleEcommerce.items = items
  },
  setLookups (state, { onlyLineSizes = false, productQuantities, productSummary, productLinesFullWidth }) {
    if (!onlyLineSizes) {
      state.productQuantities = productQuantities
      state.productSummary = productSummary
    }
    state.productLinesFullWidth = productLinesFullWidth
  },
  setMaxSingleWidthColors (state, num) {
    state.maxSingleWidthColors = num
  },
  setOrderNotes (state, orderNotes) {
    state.orderNotes = orderNotes
  },
  setOrderSummary (state, orderSummary) {
    state.orderSummary = orderSummary
  },
  setDeliveryAddress (state, deliveryAddress) {
    state.deliveryAddress = deliveryAddress
  },
  setProductQuantity (state, { productId, quantity }) {
    Vue.set(state.productQuantities, productId, quantity)
  },
  setVisibleCategory (state, { category, visible }) {
    Vue.set(state.visibleCategories, category, visible)
  },
  updateProductQuantitiesFromOrderSummary (state) {
    if (!state.orderSummary) {
      return
    }
    // Populate quantities from order summary (works even if productQuantities is empty on reload)
    state.orderSummary.orderProductsList.forEach(op => {
      if (op.productId && op.quantity > 0) {
        Vue.set(state.productQuantities, op.productId, op.quantity)
      }
    })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
